import { cancel, put, fork, select, takeLatest, takeEvery, call } from 'redux-saga/effects';
import copy from 'copy-to-clipboard';
import { getError } from 'utils/http';

import { SHIPPER_ORDER_UPDATE_START } from 'redux/modules/analytics/events';

import { ShipperOrderTracking } from 'redux/modules/analytics/task-measure';

import { TrackeableEvent } from 'redux/modules/analytics';
import { SHIPPERORDER_MODAL_OPEN } from 'redux/modules/analytics/events';

import { fetchShipperOrder as fetchShipperOrderRequest } from 'redux/modules/shipper-orders/http';

import { openManualLog } from 'redux/modules/log/effects';

import { openFormWithShipperOrder } from 'redux/modules/shipper-order-new/effects';

import {
  goToVehiclesWithFilters,
  openPage,
  openEditOrder,
} from 'redux/modules/traffic-common/effects';

import { costQuote } from 'redux/modules/journey-edit/cost-quote';

import { setFetching, setData, setError, setQuote, setQuoteLoading, setResetQuote } from './duck';

import { getState } from './selectors';
import templates from 'modules/Common/Copy/Templates';

import { setSelfChangedData$ } from 'redux/modules/realtime/effects';

import { sendEventToSegment } from 'redux/modules/analytics';

import { VIEW_VALID_VEHICLES } from 'redux/modules/analytics/events';

// @effect Action
const detailMounted$ = 'shipper-order-detail/detailMounted$';
const detailUnmounted$ = 'shipper-order-detail/detailUnmounted$';

const copyOrderDetails$ = 'shipper-order-detail/copyOrderDetails$';
const viewValidVehicles$ = 'shipper-order-detail/viewValidVehicles$';
const addManualEntryLog$ = 'shipper-order-detail/addManualEntryLog$';
const openShipper$ = 'shipper-order-detail/openShipper$';
const openUser$ = 'shipper-order-detail/openUser$';
const editOrder$ = 'shipper-order-detail/editOrder$';
const repeatOrder$ = 'shipper-order-detail/repeatOrder$';

// @effect action creators
export const detailMounted = (payload) => ({
  type: detailMounted$,
  payload,
});

export const detailUnmounted = (payload) => ({
  type: detailUnmounted$,
  payload,
});

export const copyOrderDetails = () => ({
  type: copyOrderDetails$,
  payload: {
    type: 'shipperOrder',
  },
});

export const viewValidVehicles = () => ({
  type: viewValidVehicles$,
});

export const addManualEntryLog = () => ({
  type: addManualEntryLog$,
});

export const openShipper = ({ shipper_id }) => ({
  type: openShipper$,
  payload: {
    type: 'shipper',
    shipper_id,
  },
});

export const openUser = ({ shipper_id, user_id }) => ({
  type: openUser$,
  payload: {
    type: 'shipper_user',
    shipper_id,
    user_id,
  },
});

export const editOrder = (id, redirectAfterSave) => ({
  type: editOrder$,
  payload: { id, redirectAfterSave },
});

export const repeatOrder = (id) => ({
  type: repeatOrder$,
  payload: { id },
});

let parallelTasks = [];
let mountDetailAction = null;
const openShipperOrderDetailTrackeableEvent = new TrackeableEvent();

// Workers
export function* callAndSetData(...params) {
  const shipperOrder = yield call(...params);
  yield put(setData(shipperOrder));
  return shipperOrder;
}

export function* getDetailData() {
  const { data } = yield select(getState);
  return data;
}

function* handleCostingQuote() {
  try {
    const shipperOrder = yield call(getDetailData);
    const { stops, journeys = [] } = shipperOrder;
    const journey = journeys[0] || {};
    yield put(setQuoteLoading(true));
    const quote = yield call(costQuote, {
      ...journey,
      stops,
    });
    yield put(setQuote(quote));
  } catch (err) {
    yield call(getError, err);
  } finally {
    yield put(setQuoteLoading(false));
  }
}

function* handleMounted({ payload }) {
  mountDetailAction = yield fork(mountDetail, { payload });
}

export function* mountDetail({ payload }) {
  const { id: shipperOrderId } = payload;

  yield call(openShipperOrderDetailTrackeableEvent.start, SHIPPERORDER_MODAL_OPEN, {
    shipperOrderId,
  });

  yield put(setError(null));
  yield put(setFetching(true));

  try {
    const shipperOrder = yield call(fetchShipperOrderRequest, shipperOrderId);
    yield put(setData(shipperOrder));

    parallelTasks = [yield fork(handleCostingQuote)];

    yield put(setFetching(false));
  } catch (err) {
    yield put(setError(err));
  } finally {
    yield put(setFetching(false));
    yield call(openShipperOrderDetailTrackeableEvent.end, SHIPPERORDER_MODAL_OPEN, {
      shipperOrderId,
    });
  }
}

function* copyDetails({ payload }) {
  const { type } = payload;

  try {
    const shipperOrderData = yield select(getState);

    yield call(copy, templates[type](shipperOrderData));
  } catch (err) {
    console.error(err);
  }
}

function* goToValidVehicles() {
  const shipperOrder = yield call(getDetailData);
  yield call(goToVehiclesWithFilters, shipperOrder);
  yield call(sendEventToSegment, VIEW_VALID_VEHICLES, { from: 'ShipperOrder' });
}

function* handleAddManualLogEntry() {
  const shipperOrder = yield call(getDetailData);
  yield put(
    openManualLog({
      shipperOrderId: shipperOrder.id,
    })
  );
}

export function* handleSelfChange({ payload }) {
  const shipperOrder = yield call(getDetailData) || {};
  if (!shipperOrder || !shipperOrder.id) {
    return;
  }

  const newShipperOrder = payload;

  if (!newShipperOrder) {
    return;
  }

  yield put(setData(newShipperOrder));
}

function* handleDetailUnmounted() {
  yield cancel(mountDetailAction);
  yield put(setData());

  for (let i in parallelTasks) {
    yield cancel(parallelTasks[i]);
  }

  yield put(setResetQuote());
  yield put(setFetching(true));
  yield put(setError(null));
}

function* handleRepeatOrder({ payload }) {
  const { id } = payload;
  yield put(openFormWithShipperOrder({ id }));
}

function* handleEditOrder({ payload }) {
  yield call(ShipperOrderTracking.start, payload.id, SHIPPER_ORDER_UPDATE_START);
  yield call(openEditOrder, { payload });
}

function* handlePageOpen({ payload }) {
  yield call(openPage, { payload });
}

// Watchers
export function* watchShipperOrderDetail() {
  yield takeLatest(detailMounted$, handleMounted);
  yield takeLatest(detailUnmounted$, handleDetailUnmounted);

  yield takeLatest(copyOrderDetails$, copyDetails);
  yield takeLatest(viewValidVehicles$, goToValidVehicles);
  yield takeLatest(addManualEntryLog$, handleAddManualLogEntry);
  yield takeLatest(editOrder$, handleEditOrder);
  yield takeLatest(repeatOrder$, handleRepeatOrder);

  yield takeLatest([openShipper$, openUser$], handlePageOpen);

  yield takeEvery(setSelfChangedData$('shipperorder'), handleSelfChange);
}
